import type { Mode, Op } from "../content/modes";
import { RANKS, STRAND_LEAD } from "../content/ranks";
import { TRIALS, TRIALS_VERSION, type StripeMarks } from "../content/trials";
import { enabledOps, opsForMode } from "./modes";
import { sampleQuestion, type TierMix } from "./questions/sampler";
import { typesFor } from "./questions/types";
import type { Question } from "./questions/question";
import type { Rng } from "./rng";

export const TOP_RANK_INDEX = RANKS.length - 1;

export interface TrialSpec {
  /** The rank this trial earns (1 and up; Seedling has no trial). */
  rankIndex: number;
  mode: Mode;
  seconds: number;
  /** Correct answers needed. Mix has its own, lower pass mark. */
  passMark: number;
  /** The three marks that earn stripes on the way to the pass mark. */
  stripes: StripeMarks;
  mix: TierMix;
  /** How many questions at the start are tier 1 only, before the mix takes over. */
  warmUp: number;
  /** Which version of the trial numbers this spec comes from. */
  version: number;
}

/** The trial that earns `rankIndex` in `mode`, or null for Seedling or past the top. */
export function trialFor(mode: Mode, rankIndex: number): TrialSpec | null {
  const row = TRIALS[rankIndex - 1];
  if (rankIndex < 1 || !row) return null;
  const isMix = mode === "mix";
  return {
    rankIndex,
    mode,
    seconds: row.seconds,
    passMark: isMix ? row.mixPassMark : row.passMark,
    stripes: isMix ? row.mixStripes : row.stripes,
    mix: row.mix,
    warmUp: row.warmUp,
    version: TRIALS_VERSION,
  };
}

/** The next trial for a child holding `rank`, or null at the top. */
export function nextTrial(mode: Mode, rank: number): TrialSpec | null {
  return trialFor(mode, rank + 1);
}

const WARM_UP_MIX: TierMix = { 1: 100 };

/**
 * The question source for a trial. Single-strand trials draw only from that
 * strand. Mix draws evenly from the strands the parent has switched on, so a
 * child who hasn't met times tables yet isn't tested on them. The first
 * `warmUp` questions are tier 1 only; the mix takes over after that.
 */
export function trialSource(spec: TrialSpec, ops: Record<Op, boolean>, rng: Rng) {
  const types = typesFor(opsForMode(spec.mode, ops));
  let served = 0;
  return (previous: Question | null) => {
    const mix = served++ < spec.warmUp ? WARM_UP_MIX : spec.mix;
    return sampleQuestion({ types, mix, rng, previous });
  };
}

/** Why a strand's next trial can't be started yet: the strands that must catch up, and to what. */
export interface TrialLock {
  /** The rank the strands behind must reach. */
  rankIndex: number;
  behind: Op[];
}

/**
 * Strands climb together: a strand may run only STRAND_LEAD ranks ahead of
 * the lowest other strand switched on. Null when the next trial in `mode` is
 * open. Mix is never locked and never locks a strand; a child with one
 * strand on is never locked. Ranks already held are never taken away: this
 * only decides which trial can be started next.
 */
export function trialLock(
  rank: Record<Mode, number>,
  ops: Record<Op, boolean>,
  mode: Mode,
): TrialLock | null {
  if (mode === "mix") return null;
  const target = rank[mode] + 1;
  const need = target - STRAND_LEAD;
  if (need <= 0) return null;
  const behind = enabledOps(ops).filter((op) => op !== mode && rank[op] < need);
  return behind.length ? { rankIndex: need, behind } : null;
}

/** A fresh seed for a trial attempt, stored with the result so it can be replayed. */
export function newSeed(random = Math.random): number {
  return Math.floor(random() * 2 ** 32) >>> 0;
}
